"use client";
import { ToggleButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { focusRing } from "../../utils";
import { Icon, type IconName } from "../Icon";

export type SegmentOption = {
  id: string;
  label: string;
  icon?: IconName;
};

export interface SegmentedControlProps {
  /**
   * Array of segment options
   */
  options: SegmentOption[];
  /**
   * Currently selected value
   */
  value: string;
  /**
   * Callback when selection changes
   */
  onChange: (value: string) => void;
  /**
   * Size of the control
   * @default "md"
   */
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether the control is disabled
   */
  isDisabled?: boolean;
  /**
   * Aria label for accessibility
   */
  "aria-label"?: string;
  /**
   * Visual variant of the control
   * @default "default"
   */
  variant?: "default" | "brand" | "secondary";
}

const sizeStyles = {
  sm: "h-8 text-xs",
  md: "h-10 text-sm",
  lg: "h-12 text-base",
  xl: "h-14 text-lg",
};

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
};

const getButtonClass = ({
  isSelected,
  isFirst: _isFirst,
  isLast,
  isDisabled,
  variant = "default",
}: {
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  isDisabled?: boolean;
  variant?: "default" | "brand" | "secondary";
}) => {
  const actionColor = variant === "brand" ? "action-brand" : "action-default";
  const focusRingColor =
    variant === "brand" ? "focus-visible:outline-action-brand" : "focus-visible:outline-action-default";

  // Secondary variant: lighter, more subtle styling
  if (variant === "secondary") {
    return twMerge(
      // Base styles
      focusRing(),
      "focus-visible:outline-action-default",
      "px-3 text-center transition cursor-pointer forced-color-adjust-none -ml-px first:ml-0 flex items-center justify-center h-full gap-2",

      // Selection state - light gray background for clear distinction
      isSelected
        ? "bg-background-muted text-text-heading font-medium forced-colors:!bg-[Highlight] forced-colors:!text-[HighlightText]"
        : "text-text-muted hover:bg-background-hover pressed:bg-background-hover forced-colors:!bg-[ButtonFace] forced-colors:!text-[ButtonText]",

      // Divider
      !isLast && "border-r border-border-muted",

      // Disabled state
      isDisabled &&
        "bg-background-muted text-text-disabled forced-colors:!bg-[ButtonFace] forced-colors:!text-[GrayText] border-border-muted forced-colors:border-[GrayText] cursor-not-allowed"
    );
  }

  // Default and brand variants: original bold styling
  return twMerge(
    // Base styles
    focusRing(),
    focusRingColor,
    "px-3 text-center transition cursor-pointer forced-color-adjust-none -ml-px first:ml-0 flex items-center justify-center h-full gap-2",

    // Selection state
    isSelected
      ? `bg-${actionColor} text-[color:var(--color-${actionColor}-text)] font-semibold forced-colors:!bg-[Highlight] forced-colors:!text-[HighlightText]`
      : "text-text-muted hover:bg-background-hover pressed:bg-background-hover forced-colors:!bg-[ButtonFace] forced-colors:!text-[ButtonText] hover:font-semibold",

    // Divider
    !isLast && "border-r border-border-muted",

    // Disabled state
    isDisabled &&
      "bg-background-muted text-text-disabled forced-colors:!bg-[ButtonFace] forced-colors:!text-[GrayText] border-border-muted forced-colors:border-[GrayText] cursor-not-allowed"
  );
};

/**
 * SegmentedControl
 *
 * A segmented control component for selecting between multiple options.
 * Similar to a radio group but with a more compact, button-like appearance.
 */
export function SegmentedControl({
  options,
  value,
  onChange,
  size = "md",
  className,
  isDisabled,
  variant = "default",
  "aria-label": ariaLabel,
}: SegmentedControlProps) {
  return (
    <div
      className={twMerge(
        "inline-flex overflow-hidden rounded-md",
        // Secondary variant uses thinner border
        variant === "secondary" ? "border border-border-muted" : "border-2 border-border-muted",
        sizeStyles[size],
        className
      )}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option, index) => (
        <ToggleButton
          key={option.id}
          isSelected={value === option.id}
          onChange={() => onChange(option.id)}
          className={getButtonClass({
            isSelected: value === option.id,
            isFirst: index === 0,
            isLast: index === options.length - 1,
            isDisabled,
            variant,
          })}
          isDisabled={isDisabled}
          aria-label={option.label}
        >
          {option.icon && <Icon name={option.icon} size={iconSizes[size]} />}
          <span
            className="inline-flex flex-col items-center after:content-[attr(data-label)] after:font-semibold after:h-0 after:overflow-hidden after:invisible"
            data-label={option.label}
          >
            {option.label}
          </span>
        </ToggleButton>
      ))}
    </div>
  );
}
